import React, { useState, forwardRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";

type BaseProps = {
  label?: string;
  icon?: React.ReactNode;
  error?: string;
  minRows?: number;
  containerClassName?: string;
};

type InputProps = BaseProps &
  React.InputHTMLAttributes<HTMLInputElement> & {
    type?: Exclude<React.HTMLInputTypeAttribute, "textarea">;
  };

type TextareaProps = BaseProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    type: "textarea";
  };

type GearInputProps = InputProps | TextareaProps;

const GearInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  GearInputProps
>((props, ref) => {
  const {
    label,
    type = "text",
    placeholder,
    icon,
    error,
    className,
    containerClassName,
    minRows,
    ...rest
  } = props;
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const inputType = type === "password" && showPassword ? "text" : type;

  const commonClasses = `appearance-none bg-[#331855] border rounded w-full py-2 px-3 text-white leading-tight focus:outline-none focus:shadow-outline ${
    icon ? "pl-10" : ""
  } ${className || ""}`;

  return (
    <div className={"mb-4 " + containerClassName}>
      {label && (
        <label
          htmlFor={label}
          className="block text-white text-[12px] leading-[16px] md:text-[14px] md:leading-[20px] font-[400] mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {type === "textarea" ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={label}
            placeholder={placeholder}
            rows={minRows}
            style={{
              backgroundColor: "#331855",
              border: "1px solid #6e11b04d",
            }}
            {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            className={commonClasses}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            id={label}
            type={inputType as React.HTMLInputTypeAttribute}
            placeholder={placeholder}
            style={{ backgroundColor: "#331855" }}
            {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
            className={commonClasses}
          />
        )}
        {icon && (
          <div className="absolute input-icon inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon}
          </div>
        )}
        {type === "password" && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute input-icon inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
          >
            {showPassword ? (
              <FaEye color="#6A7282" />
            ) : (
              <FaEyeSlash color="#6A7282" />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="text-xs md:text-sm text-[#ff3958] mt-[4px]">{error}</p>
      )}
    </div>
  );
});

GearInput.displayName = "GearInput";

export default GearInput;
